import React from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import {
  useGetOpenFormulareStatus,
  IOpenFormulareStatus,
} from '../hooks/reactQuerys/RatingFormQueries';
import {
  IOpenFormularePullResult,
  useOpenFormularePull,
} from '../hooks/reactQuerys/DialogInboxQueries';
import { useAlert } from '../context/AlertContext';

export interface OpenFormulareStatusViewModel {
  status: IOpenFormulareStatus | undefined;
  isLoading: boolean;
  isReady: boolean;
  isActive: boolean;
  isConfigured: boolean;
  statusLabel: string;
  statusColor: 'success' | 'warning' | 'error' | 'default';
  hint: string;
}

export const useOpenFormulareStatusViewModel =
  (): OpenFormulareStatusViewModel => {
    const { ofStatus, isLoadingOFStatus } = useGetOpenFormulareStatus();

    const isActive = ofStatus?.isActive ?? false;
    const isConfigured = ofStatus?.isConfigured ?? false;
    const isReady = ofStatus?.isReady ?? false;

    let statusLabel = 'Unbekannt';
    let statusColor: OpenFormulareStatusViewModel['statusColor'] = 'default';
    let hint = '';

    if (isLoadingOFStatus) {
      statusLabel = 'Wird geprüft …';
      statusColor = 'default';
    } else if (!isActive) {
      statusLabel = 'Deaktiviert';
      statusColor = 'warning';
      hint =
        'Die Erweiterung „OpenFormulare" ist nicht aktiviert. Aktivieren Sie sie unter Einstellungen → Erweiterungen.';
    } else if (!isConfigured) {
      statusLabel = 'Nicht konfiguriert';
      statusColor = 'warning';
      hint =
        'Die Erweiterung ist aktiviert, aber noch nicht vollständig eingerichtet. Bitte die Verbindungsdaten unter Einstellungen → Erweiterungen pflegen.';
    } else if (!isReady) {
      statusLabel = 'Nicht verbunden';
      statusColor = 'error';
      hint = 'Verbindung zu OpenFormulare ist derzeit nicht möglich.';
    } else {
      statusLabel = 'Verbunden';
      statusColor = 'success';
      hint = 'OpenFormulare ist aktiv und einsatzbereit.';
    }

    return {
      status: ofStatus,
      isLoading: isLoadingOFStatus,
      isReady,
      isActive,
      isConfigured,
      statusLabel,
      statusColor,
      hint,
    };
  };

interface ICardProps {
  onPullComplete?: () => void;
}

export const OpenFormulareCard: React.FC<ICardProps> = ({ onPullComplete }) => {
  const { showAlert } = useAlert();
  const vm = useOpenFormulareStatusViewModel();
  const { pullOpenFormulare, isPullingOpenFormulare } = useOpenFormularePull();

  const handleClick = async () => {
    if (!vm.isReady) return;
    try {
      const result = (await pullOpenFormulare()) as IOpenFormularePullResult;
      if (result.status === 'ok') {
        if (result.imported > 0) {
          showAlert({
            text: `${result.imported} neue${result.imported === 1 ? 'r Eintrag wurde' : ' Einträge wurden'} aus OpenFormulare übernommen.`,
            severity: 'success',
          });
        } else {
          showAlert({
            text: 'Keine neuen Einträge in OpenFormulare vorhanden.',
            severity: 'info',
          });
        }
        onPullComplete?.();
      } else {
        showAlert({
          text:
            result.message ?? 'OpenFormulare konnte nicht abgerufen werden.',
          severity: 'warning',
        });
      }
    } catch (e) {
      console.error(e);
      showAlert({
        text: 'Abruf von OpenFormulare fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  const disabled = !vm.isReady || isPullingOpenFormulare;

  const card = (
    <Card
      variant='outlined'
      sx={{
        flex: '1 1 280px',
        minWidth: 260,
        maxWidth: 380,
        display: 'flex',
        borderColor: vm.isReady ? 'success.light' : undefined,
        bgcolor: vm.isReady ? 'action.hover' : undefined,
        opacity: disabled && !isPullingOpenFormulare ? 0.7 : 1,
      }}
    >
      <CardActionArea
        onClick={handleClick}
        disabled={disabled}
        sx={{ height: '100%', alignItems: 'stretch' }}
      >
        <CardContent>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={{ mb: 1 }}
          >
            <Stack direction='row' alignItems='center' spacing={1}>
              <CloudSyncIcon
                fontSize='small'
                color={vm.isReady ? 'success' : 'action'}
              />
              <Typography variant='subtitle1'>OpenFormulare</Typography>
            </Stack>
            {isPullingOpenFormulare ? (
              <CircularProgress size={18} />
            ) : (
              <Chip
                size='small'
                label={vm.statusLabel}
                color={vm.statusColor}
              />
            )}
          </Stack>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            Dialog-Editor für Mandantenformulare. Bei aktiver Anbindung werden
            ausgefüllte Formulare automatisch nach DiNo übertragen. Klicken Sie
            hier, um neue Einträge jetzt manuell abzurufen.
          </Typography>
          {vm.hint && (
            <Typography variant='caption' color='text.secondary'>
              {vm.hint}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );

  if (disabled && !vm.isReady) {
    return <Tooltip title={vm.hint}>{card}</Tooltip>;
  }
  return card;
};
